import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer, ReferenceLine } from 'recharts';
import Card, { CardHeader, CardBody } from '../ui/Card';

interface CashFlowWaterfallProps {
  data: {
    operatingCashFlow: number;
    investingCashFlow: number;
    financingCashFlow: number;
    period: string;
  };
}

const formatCurrency = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (absValue >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  return `$${value.toFixed(0)}`;
};

export default function CashFlowWaterfall({ data }: CashFlowWaterfallProps) {
  const netCashChange = data.operatingCashFlow + data.investingCashFlow + data.financingCashFlow;

  // Build waterfall data
  const waterfallData = [
    {
      name: 'Operating CF',
      value: data.operatingCashFlow / 1e9,
      absolute: data.operatingCashFlow / 1e9,
      fill: '#10b981' // green
    },
    {
      name: 'Investing CF',
      value: data.investingCashFlow / 1e9,
      absolute: (data.operatingCashFlow + data.investingCashFlow) / 1e9,
      fill: data.investingCashFlow > 0 ? '#10b981' : '#ef4444' // green or red
    },
    {
      name: 'Financing CF',
      value: data.financingCashFlow / 1e9,
      absolute: netCashChange / 1e9,
      fill: data.financingCashFlow > 0 ? '#10b981' : '#ef4444'
    },
    {
      name: 'Net Change',
      value: netCashChange / 1e9,
      absolute: netCashChange / 1e9,
      fill: netCashChange > 0 ? '#3b82f6' : '#ef4444' // blue or red
    }
  ];

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-neutral-900">Cash Flow Waterfall</h3>
          <span className="text-xs font-medium text-neutral-400 bg-neutral-50 px-2.5 py-1 rounded-full">{data.period}</span>
        </div>
      </CardHeader>
      <CardBody>
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { label: 'Operating', value: data.operatingCashFlow, isPositive: data.operatingCashFlow > 0 },
              { label: 'Investing', value: data.investingCashFlow, isPositive: data.investingCashFlow > 0 },
              { label: 'Financing', value: data.financingCashFlow, isPositive: data.financingCashFlow > 0 },
              { label: 'Net Change', value: netCashChange, isPositive: netCashChange > 0, isNet: true },
            ].map(({ label, value, isPositive, isNet }) => (
              <div key={label} className={`p-2.5 border rounded-lg ${
                isNet
                  ? (isPositive ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200')
                  : (isPositive ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200')
              }`}>
                <div className={`text-[10px] font-medium mb-0.5 ${
                  isNet
                    ? (isPositive ? 'text-blue-600' : 'text-red-600')
                    : (isPositive ? 'text-green-600' : 'text-red-600')
                }`}>{label}</div>
                <div className={`text-sm font-bold tabular-nums ${
                  isNet
                    ? (isPositive ? 'text-blue-900' : 'text-red-900')
                    : (isPositive ? 'text-green-900' : 'text-red-900')
                }`}>{formatCurrency(value)}</div>
              </div>
            ))}
          </div>

          {/* Waterfall Chart */}
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={waterfallData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis label={{ value: 'Billions ($)', angle: -90, position: 'insideLeft' }} />
              <Tooltip
                formatter={(value: number) => `$${value.toFixed(1)}B`}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e5e5' }}
              />
              <ReferenceLine y={0} stroke="#000" />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {waterfallData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardBody>
    </Card>
  );
}
